// Intereses del préstamo apuntados por la app sin pedirlos: cuando el mes en
// curso o el anterior tiene una letra (traspaso entrante desde cualquier
// cuenta) y ningún interés, se crea el movimiento «Intereses del
// préstamo» (nota «Intereses de <mes>») fechado el día de la letra. Los
// meses más antiguos solo con confirmación en la ficha de la deuda.
//
// Idempotente: el id del movimiento sale de la cuenta y el mes (UUID v5), así
// que dos pestañas o dos dispositivos que lo creen a la vez escriben la MISMA
// fila (el CRDT las funde) y nunca hay dos intereses del mismo mes.
//
// Un interés automático que Vadym BORRA no se vuelve a crear: el borrado deja
// la fila con ese id (tombstone) y reescribirla no la resucita (el campo
// `tombstone` no se toca al insertar), así que antes se miran los ids que ya
// existen, vivos o borrados, y esos se dejan en paz.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { PAYEE_INTERESES } from '@actual-app/core/shared/prestamos';
import type { DatosPrestamo } from '@actual-app/core/shared/prestamos';
import { q } from '@actual-app/core/shared/query';
import type { AccountEntity } from '@actual-app/core/types/models';
import type { TFunction } from 'i18next';
import { v5 as uuidv5 } from 'uuid';

import { useLanguage } from '#hooks/useLocale';
import { usePayees } from '#hooks/usePayees';
import { aqlQuery } from '#queries/aqlQuery';

import { repartirPendientes } from './movimientosDeuda';
import type { InteresNuevo, MovimientoDeuda } from './movimientosDeuda';
import { textoMes } from './textos';

const ESPACIO_INTERESES = 'b6f1c0de-7a1e-4c2b-9d3e-1f2a3b4c5d6e';

/** Id fijo del interés automático de una cuenta y un mes (`YYYY-MM`). */
export function idInteresAutomatico(accountId: string, mes: string): string {
  return uuidv5(`intereses:${accountId}:${mes}`, ESPACIO_INTERESES);
}

/** Nota que pone la app a sus intereses: «Intereses de septiembre de 2026». */
export function notaInteres(t: TFunction, mes: string, idioma: string) {
  return t('Interest for {{month}}', { month: textoMes(mes, idioma) });
}

/**
 * Notas que se reconocen como de la app para un mes: la del idioma actual y
 * las de español, catalán e inglés (por si se cambió de idioma).
 */
export function notasDeLaApp(
  t: TFunction,
  idioma: string,
): (mes: string) => string[] {
  return mes => [
    notaInteres(t, mes, idioma),
    `Intereses de ${textoMes(mes, 'es-ES')}`,
    `Interessos (${textoMes(mes, 'ca-ES')})`,
    `Interest for ${textoMes(mes, 'en-US')}`,
  ];
}

/** Meses que ya se están apuntando en esta pestaña (`cuenta:mes`). */
const enCurso = new Set<string>();
/** Meses cuyo interés automático ya existe o se borró (`cuenta:mes`). */
const yaExistentes = new Set<string>();

/**
 * Quita de `pendientes` los meses cuyo id fijo ya está en la base (vivo o
 * borrado): un interés automático borrado a mano no se vuelve a apuntar.
 */
export function sinIdsExistentes<T extends { mes: string }>(
  accountId: string,
  pendientes: readonly T[],
  idsExistentes: ReadonlySet<string>,
): T[] {
  return pendientes.filter(
    p => !idsExistentes.has(idInteresAutomatico(accountId, p.mes)),
  );
}

type Payee = { id: string; name: string };

/** Ids fijos de esos meses que ya existen en la base (vivos o borrados). */
async function idsExistentes(
  accountId: string,
  lista: readonly InteresNuevo[],
): Promise<Set<string>> {
  const ids = lista.map(p => idInteresAutomatico(accountId, p.mes));
  const { data: filas } = await aqlQuery(
    q('transactions')
      .filter({ id: { $oneof: ids } })
      .withDead()
      .options({ splits: 'none' })
      .select(['id']),
  );
  return new Set((filas as { id: string }[]).map(f => f.id));
}

/**
 * Escribe los intereses de `lista` que aún no existen (ni borrados). Los que
 * ya existían quedan en `yaExistentes` para no volver a mirarlos.
 */
async function apuntarIntereses(
  accountId: string,
  lista: readonly InteresNuevo[],
  payees: readonly Payee[],
  nota: (mes: string) => string,
): Promise<void> {
  if (lista.length === 0) {
    return;
  }
  const nuevos = sinIdsExistentes(
    accountId,
    lista,
    await idsExistentes(accountId, lista),
  );
  lista
    .filter(p => !nuevos.includes(p))
    .forEach(p => yaExistentes.add(`${accountId}:${p.mes}`));
  if (nuevos.length === 0) {
    return;
  }
  const existente = payees.find(p => p.name === PAYEE_INTERESES);
  const payee =
    existente?.id ?? (await send('payee-create', { name: PAYEE_INTERESES }));
  await send('transactions-batch-update', {
    added: nuevos.map(p => ({
      id: idInteresAutomatico(accountId, p.mes),
      account: accountId,
      date: p.fecha,
      amount: -p.importe,
      payee,
      notes: nota(p.mes),
    })),
  });
}

/**
 * Intereses que faltan en la cuenta del préstamo. Los del mes en curso y el
 * anterior se apuntan solos; los de meses más antiguos se devuelven
 * (`anteriores`) para pedir confirmación en la ficha (`apuntarAnteriores`).
 * No hace nada sin TIN, mientras cargan los movimientos ni en cuentas
 * sincronizadas con el banco (el banco ya trae sus intereses).
 */
export function useInteresesAutomaticos(
  account: AccountEntity,
  datos: DatosPrestamo | null,
  movimientos: readonly MovimientoDeuda[],
  cargando: boolean,
): {
  anteriores: InteresNuevo[];
  apuntarAnteriores: () => Promise<void>;
} {
  const { t } = useTranslation();
  const idioma = useLanguage();
  const { data: payees, isLoading: cargandoPayees } = usePayees();
  const sincronizada = !!account.account_sync_source;
  const [, setRevision] = useState(0);

  const reparto =
    cargando || sincronizada || account.closed
      ? { automaticos: [], anteriores: [] }
      : repartirPendientes(
          movimientos,
          datos?.interes_anual ?? null,
          monthUtils.currentDay(),
          datos?.desde,
        );
  const libre = (p: InteresNuevo) =>
    !enCurso.has(`${account.id}:${p.mes}`) &&
    !yaExistentes.has(`${account.id}:${p.mes}`);
  const pendientes = reparto.automaticos.filter(libre);
  const anteriores = reparto.anteriores.filter(libre);
  const clave = pendientes.map(p => `${p.mes}:${p.importe}`).join('|');
  const claveAnteriores = anteriores.map(p => p.mes).join('|');

  useEffect(() => {
    if (pendientes.length === 0 || cargandoPayees || payees == null) {
      return;
    }
    const claves = pendientes.map(p => `${account.id}:${p.mes}`);
    claves.forEach(k => enCurso.add(k));
    void apuntarIntereses(account.id, pendientes, payees, mes =>
      notaInteres(t, mes, idioma),
    ).finally(() => {
      // Se suelta al rato: para entonces la consulta ya trae el movimiento
      // y el mes deja de estar pendiente.
      setTimeout(() => claves.forEach(k => enCurso.delete(k)), 5000);
    });
    // `clave` resume `pendientes`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, account.id, cargandoPayees, payees == null]);

  // Los anteriores borrados a mano no se ofrecen otra vez.
  useEffect(() => {
    if (anteriores.length === 0) {
      return;
    }
    let vigente = true;
    void idsExistentes(account.id, anteriores).then(ids => {
      const ya = anteriores.filter(p =>
        ids.has(idInteresAutomatico(account.id, p.mes)),
      );
      if (vigente && ya.length > 0) {
        ya.forEach(p => yaExistentes.add(`${account.id}:${p.mes}`));
        setRevision(r => r + 1);
      }
    });
    return () => {
      vigente = false;
    };
    // `claveAnteriores` resume `anteriores`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveAnteriores, account.id]);

  const apuntarAnteriores = useCallback(async () => {
    if (payees == null) {
      return;
    }
    await apuntarIntereses(account.id, anteriores, payees, mes =>
      notaInteres(t, mes, idioma),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveAnteriores, account.id, payees, t, idioma]);

  return { anteriores, apuntarAnteriores };
}
